// src/components/Notifications/PetNotifications.tsx

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { Badge, IconButton, Popover, Typography, Button, Stack } from '@mui/material';

type NotificationType = {
  id: string;
  petId: string;
  userId: string;
  requestedBy: string;
  status: 'PENDING' | 'ACCEPTED' | 'DECLINED';
  createdAt: string;
  updatedAt: string;
  pet: {
    name: string;
  };
  sender: {
    name: string;
  };
};

type Props = {
  userId: string;
  token: string;
  onAcceptedOrRejected: () => void;
};

export default function PetNotifications({ userId, token, onAcceptedOrRejected }: Props) {
  const [notifications, setNotifications] = useState<NotificationType[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const fetchNotifications = async () => {
    const url = `${process.env.REACT_APP_URL}/subtutorInvites/invitations/${userId}`;
    try {
      const res = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setNotifications(res.data.invitations);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  };

  const handleAccept = async (notificationId: string) => {
    await axios.post(`${process.env.REACT_APP_URL}/subtutorInvites/respond`, {
      requestId: notificationId,
      action: 'ACCEPT'
    }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
    onAcceptedOrRejected();
  };

  const handleReject = async (notificationId: string) => {
    await axios.post(`${process.env.REACT_APP_URL}/subtutorInvites/respond`, {
      requestId: notificationId,
      action: 'DECLINE'
    }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
    onAcceptedOrRejected();
  };

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const open = Boolean(anchorEl);
  const id = open ? 'notification-popover' : undefined;

  return (
    <>
      <IconButton onClick={handleOpen} style={{ position: 'absolute', top: 10, right: 10 }}>
        {notifications && notifications.length > 0 ? (
          <Badge badgeContent={notifications.length} color="error">
            <NotificationsIcon fontSize="large" />
          </Badge>
        ) : (
          <NotificationsIcon fontSize="large" />
        )}
      </IconButton>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
      >
        <div style={{ padding: '16px', maxWidth: '300px' }}>
          <Typography variant="h6">Notificaciones</Typography>
          { notifications === undefined || notifications.length === 0 ? (
            <Typography variant="body2">No tienes notificaciones</Typography>
          ) : (
            notifications.map((notif) => (
              <div key={notif.id} style={{ marginTop: '12px' }}>
                <Typography variant="body1">
                  {notif.sender.name} te invitó a ser parte de la familia de {notif.pet.name}
                </Typography>
                <Stack direction="row" spacing={1} marginTop={1}>
                  <Button variant="contained" size="small" onClick={() => handleAccept(notif.id)}>Aceptar</Button>
                  <Button variant="outlined" size="small" onClick={() => handleReject(notif.id)}>Rechazar</Button>
                </Stack>
              </div>
            ))
          )}
        </div>
      </Popover>
    </>
  );
}
